import { pathOr } from 'ramda';
import { GQLQueryBuilder, QUERY_OPERATION_MUTATION, QueryData } from './queryBuilder';
import { requestGraphQL } from './useGraphQLQuery';
import { isEmptyOrNil, isNotEmpty } from 'src/utilities/hasValue';

export const TEAM_NAMES = `query describeTeams($accountId:bigint!) {
    teams(where: {account_id: {_eq: $accountId}}) {
       name:full_name
       id
      }
  }`;


export const ALL_TEAMS_LIST = `teams(where: {object_meta: {_contains: $objectMeta}, full_name: {_neq: "base_team"}}) {
      id
      full_name
      description
      object_meta
      account_id
      metadata
      updated_at
      created_at
      team_members {
        id
        object_meta
        metadata
        member_owner_id
        updated_at
        created_at
      }
  }`;

export const TEAMS_LIST_BY_ACCOUNTS = `teams(where: {object_meta: {_contains: $objectMeta}, full_name: {_neq: "base_team"}, account_id: {_in: $accountIds}}) {
  id
  full_name
  description
  object_meta
  created_at
  updated_at
  metadata
  team_members {
    id
    object_meta
    member_owner_id
    metadata
    created_at
    updated_at
  }
}`;



const UPSERT_TEAMS = `insert_teams(objects: $teams , on_conflict: {constraint: teams_pkey, update_columns: [full_name, description, object_meta]}) {
  returning {
    id
    full_name
    object_meta
    description
    created_at
  }
}`;

const UPSERT_TEAM_MEMBERS = `insert_team_members(objects: $members, on_conflict: {constraint: team_members_pkey, update_columns: [object_meta, member_owner_id, metadata]}) {
  returning {
    id
    team_id
    member_owner_id
    object_meta
    metadata
    created_at
  }
}`;

const DELETE_TEAM_MEMBERS = `delete_team_members(where: {id: {_in: $deleteMemberIds}}) {
  affected_rows
  returning{
    id
  }
}`

export const BULK_DELETE_TEAM_MEMBERS_BY_ID = `mutation delete_team_members($teamsId: [bigint!]) {
  delete_team_members(where: {id: {_in: $teamsId}}) {
    affected_rows
    returning{
      id
    }
  }
}`

const BULK_DELETE_TEAM_AND_MEMBERS = `mutation delete_team_and_members($teamsId: [bigint!]) {
    delete_team_members(where: {team_id: {_in: $teamsId}}) {
      affected_rows
      returning{
        id
      }
    }
    delete_teams(where: {id: {_in: $teamsId}}) {
      affected_rows
      returning {
        id
        name:full_name
        created_at
      }
    }
  }`;


const buildQuery = (data: any) => {
  const { members, deleteMemberIds } = data;

  const queries: QueryData[] = [];

  const membersMutate: QueryData = {
    name: 'team_members_upsert',
    query: UPSERT_TEAM_MEMBERS,
    variables: {
      members
    }
  }

  const membersDelete: QueryData = {
    name: 'delete_members',
    query: DELETE_TEAM_MEMBERS,
    variables: {
      deleteMemberIds
    }
  }

  if (isNotEmpty(deleteMemberIds)) {
    queries.push(membersDelete)
  }

  if (isNotEmpty(members)) {
    queries.push(membersMutate)
  }

  const queryBuilder = new GQLQueryBuilder(queries, 'mutateTeamAndMembers', QUERY_OPERATION_MUTATION)
  return {
    query: queryBuilder.getQueryString(),
    variables: queryBuilder.getQueryVariables()
  }
}

export const GQLDeleteWholeTeamAndMembers = async (
  teamId: any,
  onSuccess?: (_: any) => void,
  onError?: (_: any) => void,
) => {
  return new Promise(async (resolve, reject) => {
    try {
      const result = await requestGraphQL(
        BULK_DELETE_TEAM_AND_MEMBERS,
        {
          teamsId: [teamId]
        });
      const error = pathOr([], ['errors'], result);
      if (onSuccess && !isNotEmpty(error)) {
        onSuccess(result)
        resolve(result)
      }
      if (onError && isNotEmpty(error)) {
        onError(error)
        reject(error)
      }
    } catch (err: any) {
      onError && onError(err)
      reject(err)
    }
  })
};


export const GQLUpsertTeam = (data: {
  team: any,
  onSuccess?: (_: any) => void,
  onError?: (_: any) => void,
}) => {
  const { team, onSuccess, onError } = data;
  const teamCreateQuery: QueryData = {
    name: 'create_teams',
    query: UPSERT_TEAMS,
    variables: {
      teams: [team]
    }
  }
  const queryBuilder = new GQLQueryBuilder([teamCreateQuery], 'upsertTeams', QUERY_OPERATION_MUTATION)
  return requestGraphQL(
    queryBuilder.getQueryString(),
    queryBuilder.getQueryVariables()
  ).then((data: any) => {
    onSuccess && onSuccess(data);
    return data
  }).catch((err: any) => {
    onError && onError(err)
  })
}

export const GQLUpsertTeamAndMembers = async (
  data: {
    members: any,
    deleteMemberIds: string[]
  },
  onSuccess?: (_: any) => void,
  onError?: (_: any) => void,
) => {
  const { deleteMemberIds } = data;
  return new Promise(async (resolve, reject) => {
    const { query, variables } = buildQuery(data)
    if (isEmptyOrNil(variables)) {
      const emptyResult = {
        members: [],
        deleteMemberIds: []
      }
      onSuccess && onSuccess(emptyResult)
      resolve(emptyResult)
      return
    }
    try {
      const result = await requestGraphQL(query, variables);
      const error = pathOr([], ['data', 'error'], result);
      if (onSuccess && !isNotEmpty(error)) {
        const updatedMembers = pathOr([], ['data', 'insert_team_members', 'returning'], result)
        const res = {
          members: updatedMembers,
          deleteMemberIds
        }
        onSuccess(res)
        resolve(res)
        return
      }
      if (onError && isNotEmpty(error)) {
        onError(error)
        reject(error)
        return
      }
      resolve(result)
    } catch (err: any) {
      onError && onError(err)
      reject(err)
    }
  })
};
